import { Component, AfterViewInit, ViewChild, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';

// Importaciones de servicios.
import { LicenseService } from '../../../services/license.service';

// Importaciones de componentes.
import { LicenseCreateDialogComponent } from '../../../components/license-create-dialog/license-create-dialog.component';
import { LicenseDeleteDialogComponent } from '../../../components/license-delete-dialog/license-delete-dialog.component';
import { LicenseUpdateDialogComponent } from '../../../components/license-update-dialog/license-update-dialog.component';

// Importaciones de interfaces.
import { ILicense } from '../../../interfaces/license.interface';
import { ILicenseResponse } from '../../../interfaces/license-response.interface';

// Importaciones de modelos.
import { License } from '../../../models/license.model';
import { Product } from '../../../models/product.model';

/**
 * Componente de configuración de las licencias.
 *
 * Este componente proporciona una interfaz para gestionar la configuración de las licencias dentro de la aplicación.
 * Permite realizar ajustes relacionados con las licencias, como actualizar información, eliminar o crear.
 */
@Component({
  selector: 'app-license-settings',
  templateUrl: './license-settings.component.html',
  standalone: false,
})
export class LicenseSettingsComponent implements OnInit, AfterViewInit {
  /**
   * Indica si las licencias están siendo cargados.
   *
   * Esta propiedad se utiliza para mostrar un indicador de carga mientras
   * se recuperan las licencias de la fuente de datos.
   */
  loading: boolean = false;

  /**
   * Columnas que se mostrarán en la tabla de las licencias.
   *
   * Esta propiedad define las columnas que se mostrarán en la tabla de las licencias, incluyendo los campos
   * relacionados con la información básica de la licencia.
   */
  tableColumns: string[] = [
    'alias',
    'name',
    'type',
    'product.name',
    'disabled',
    'updatedAt',
    'createdAt',
    'actions',
  ];

  /**
   * Datos de la tabla de las licencias.
   *
   * Esta propiedad contiene los datos de las licencias que se mostrarán en la tabla, utilizando `MatTableDataSource`
   * para manejar y mostrar los datos de manera eficiente, incluyendo funcionalidades como paginación y filtrado.
   */
  tableData: MatTableDataSource<ILicense> = new MatTableDataSource<ILicense>();

  /**
   * Paginador de la tabla de las licencias.
   *
   * Esta propiedad se utiliza para la paginación de los datos en la tabla de las licencias. Se hace referencia a un
   * componente `MatPaginator` que permite navegar entre las diferentes páginas de la tabla.
   */
  @ViewChild(MatPaginator) tablePaginator!: MatPaginator;

  /**
   * Referencia al componente MatSort que se utiliza para habilitar la funcionalidad de ordenación en la tabla.
   *
   * Se utiliza el decorador `@ViewChild` para acceder a la instancia del componente MatSort asociado a la tabla de las licencias.
   * Esto permite aplicar la funcionalidad de ordenación en las columnas de la tabla, permitiendo a los usuarios ordenar los
   * datos de forma ascendente o descendente según la columna seleccionada.
   *
   * @see MatSort - Componente de Angular Material que proporciona capacidades de ordenación para las tablas.
   */
  @ViewChild(MatSort) tableSort!: MatSort;

  /**
   * Constructor del componente.
   *
   * Este constructor inyecta las dependencias necesarias para el funcionamiento del componente.
   *
   * @param licenseService - Servicio para gestionar la información de las licencias.
   * @param matDialog - Servicio para abrir cuadros de diálogo.
   * @param snackBar - Servicio para mostrar notificaciones de tipo snack bar.
   */
  constructor(
    private licenseService: LicenseService,
    private matDialog: MatDialog,
    private snackBar: MatSnackBar
  ) {}

  /**
   * Método del ciclo de vida de Angular que se ejecuta una vez que el componente
   * ha sido inicializado, pero antes de que se renderice la vista en el DOM.
   *
   * Este método se utiliza para inicializar datos o realizar cualquier operación
   * que deba llevarse a cabo antes de que el componente sea visible para el usuario.
   * En este caso, se llama a la función `initTable()` para inicializar y cargar
   * los datos necesarios para la tabla.
   */
  ngOnInit(): void {
    this.initTable();
  }

  /**
   * Método del ciclo de vida de Angular que se ejecuta después de que
   * la vista del componente y sus vistas hijas hayan sido completamente inicializadas.
   *
   * Este método se utiliza para realizar inicializaciones adicionales que
   * requieren acceso a las vistas del componente (como elementos DOM,
   * directivas, o componentes hijos).
   */
  ngAfterViewInit(): void {
    this.configureTable();
  }

  /**
   * Inicializa la tabla de las licencias.
   *
   * Este método se encarga de configurar y cargar los datos necesarios para la tabla de las licencias.
   * Puede incluir la obtención de datos desde un servicio, la configuración de la paginación
   * y otras configuraciones relacionadas con la visualización de la tabla.
   */
  private async initTable(): Promise<void> {
    this.loading = true;
    this.tableData = new MatTableDataSource<ILicense>();

    try {
      // Obtener las licencias y añadir-las a la tabla.
      const licenseResponse: ILicenseResponse =
        await this.licenseService.getAll();

      this.tableData = new MatTableDataSource<ILicense>(
        licenseResponse.licenses.map((license: ILicense) => {
          return new License({
            ...license,
            product: new Product(license.product),
          });
        })
      );
    } finally {
      this.configureTable();
      this.loading = false;
    }
  }

  /**
   * Configura la tabla de datos con las opciones necesarias, como la ordenación y la paginación.
   *
   * Se asegura de que la tabla esté configurada correctamente para su visualización y que las
   * funcionalidades de paginación y ordenación estén habilitadas, si es necesario.
   */
  private configureTable(): void {
    this.tableData.data?.reverse();

    // Asignar el `paginator` y el `sort` si existen.
    this.tablePaginator && (this.tableData.paginator = this.tablePaginator);
    this.tableSort && (this.tableData.sort = this.tableSort);
  }

  /**
   * Recarga la tabla de las licencias.
   *
   * Esta función se encarga de actualizar la vista de la tabla de las licencias
   * para reflejar los cambios recientes en los datos. Puede ser útil después
   * de realizar acciones como agregar, editar o eliminar licencias, asegurando
   * que la interfaz siempre muestre la información más actualizada.
   */
  reload(): void {
    this.initTable();
  }

  /**
   * Crea una nueva licencia.
   *
   * Esta función abre un diálogo en la interfaz de usuario para permitir al
   * usuario ingresar los datos de la nueva licencia. Una vez que el usuario
   * ha completado el formulario y confirmado la acción, la función recoge
   * la información ingresada y crea un nuevo registro de licencia. Si la
   * creación es exitosa, se actualiza la lista de licencias.
   */
  create(): void {
    this.matDialog
      .open(LicenseCreateDialogComponent, {
        width: '500px',
        maxWidth: '100%',
      })
      .afterClosed()
      .subscribe((isCreated: boolean) => {
        if (isCreated) {
          this.initTable();
        }
      });
  }

  /**
   * Actualiza la información de una licencia.
   *
   * Este método se encarga de actualizar los datos de una licencia específica, generalmente a través
   * de una llamada a un servicio para realizar la actualización en la base de datos o en el servidor.
   * El parámetro `license` contiene los nuevos valores que se deben guardar.
   *
   * @param license - El objeto que contiene la información de la licencia.
   */
  update(license: ILicense) {
    this.matDialog
      .open(LicenseUpdateDialogComponent, {
        width: '500px',
        maxWidth: '100%',
        data: license,
      })
      .afterClosed()
      .subscribe((isUpdated: boolean) => {
        if (isUpdated) {
          this.initTable();
        }
      });
  }

  /**
   * Elimina una licencia de la lista.
   *
   * Abre un diálogo de confirmación para eliminar una licencia de la lista.
   * Si el usuario confirma la acción, la licencia es eliminada.
   *
   * @param license - El objeto licencia que se va a eliminar.
   */
  delete(license: ILicense): void {
    this.matDialog
      .open(LicenseDeleteDialogComponent, {
        width: '500px',
        maxWidth: '100%',
        data: license,
      })
      .afterClosed()
      .subscribe((isDeleted: boolean) => {
        if (isDeleted) {
          this.initTable();
        }
      });
  }

  /**
   * Actualiza el estado de `license.disabled` y lo guarda en la base de datos.
   * Revierte el cambio si ocurre un error.
   *
   * @param license - Objeto a actualizar.
   * @param isChecked - Nuevo estado del toggle.
   */
  async toggleStatus(license: ILicense, isChecked: boolean): Promise<void> {
    const previousValue = license.disabled;
    license.disabled = Number(!isChecked);

    try {
      const licenseResponse: string = await this.licenseService.update(license);
      this.snackBar.open(licenseResponse, '', {
        duration: 5000,
      });
    } catch (errors: any) {
      license.disabled = previousValue;
    }
  }
}
